// 🔧 engagement_funnel.js
// ✅ Purpose: Business Intelligence funnel for Engagement Passport activity
// ✅ Features:
// - Shows Passport engagement funnel for the current event
// - Measures Passport Viewed, Stop Viewed, First Stop, Five Stops, Completed, and Achievements
// - Uses engagement_events produced by the Secure Engagement Gateway
// - Calculates unique users at each funnel stage
// - Displays conversion percentages
// - Responsive Bootstrap layout
// - Fully block-commented for clarity and future devs

import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  orderBy,
  query,
  where,
} from "firebase/firestore";

import { db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from "../../utils/logger";

const getUserId = (event = {}) => {
  return event.userId || event.uid || "";
};

const getPercent = (value, total) => {
  if (!total) return 0;

  return Math.round((value / total) * 100);
};

const EngagementFunnel = () => {
  const {
    ready,
    eventLoading,
    eventId,
  } = useEventContext({
    redirectIfNotFound: true,
  });

  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);

  const loadEvents = async () => {
    if (!eventId) {
      setEvents([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const snap = await getDocs(
        query(
          collection(db, "events", eventId, "engagement_events"),
          orderBy("occurredAt", "asc")
        )
      );

      setEvents(
        snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }))
      );
    } catch (err) {
      logger.error("❌ Failed to load engagement funnel:", {
        value: err,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!ready || eventLoading) return;

    loadEvents();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, eventLoading, eventId]);

  const funnel = useMemo(() => {
    const viewedPassport = new Set();
    const viewedStop = new Set();
    const completedOneStop = new Set();
    const completedFiveStops = new Set();
    const completedPassport = new Set();
    const unlockedAchievement = new Set();

    const completedStopsByUser = new Map();

    events.forEach((event) => {
      const userId = getUserId(event);

      if (!userId) return;

      if (event.eventType === "passport_viewed") {
        viewedPassport.add(userId);
      }

      if (event.eventType === "passport_stop_viewed") {
        viewedStop.add(userId);
      }

      if (event.eventType === "activity_completed") {
        const current = completedStopsByUser.get(userId) || 0;
        const next = current + 1;

        completedStopsByUser.set(userId, next);
        completedOneStop.add(userId);

        if (next >= 5) {
          completedFiveStops.add(userId);
        }
      }

      if (event.eventType === "passport_completed") {
        completedPassport.add(userId);
      }

      if (event.eventType === "achievement_unlocked") {
        unlockedAchievement.add(userId);
      }
    });

    const total = viewedPassport.size;

    return [
      {
        key: "passport_viewed",
        label: "Passport Viewed",
        description: "Attendees who opened the Passport.",
        count: viewedPassport.size,
        percent: 100,
      },
      {
        key: "passport_stop_viewed",
        label: "Stop Viewed",
        description: "Attendees who opened at least one Passport Stop.",
        count: viewedStop.size,
        percent: getPercent(viewedStop.size, total),
      },
      {
        key: "first_stop",
        label: "First Stop Collected",
        description: "Attendees who collected at least one stop.",
        count: completedOneStop.size,
        percent: getPercent(completedOneStop.size, total),
      },
      {
        key: "five_stops",
        label: "Five Stops Collected",
        description: "Attendees who collected five or more stops.",
        count: completedFiveStops.size,
        percent: getPercent(completedFiveStops.size, total),
      },
      {
        key: "passport_completed",
        label: "Passport Completed",
        description: "Attendees who collected all active stops.",
        count: completedPassport.size,
        percent: getPercent(completedPassport.size, total),
      },
      {
        key: "achievement_unlocked",
        label: "Achievement Unlocked",
        description: "Attendees who unlocked at least one achievement.",
        count: unlockedAchievement.size,
        percent: getPercent(unlockedAchievement.size, total),
      },
    ];
  }, [events]);

  if (!ready || eventLoading || loading) {
    return <FullPageSpinner message="Loading Engagement Funnel..." />;
  }

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1100px" }}>
        <div className="mb-4">
          <h1 className="display-5 mb-2">
            📊 Engagement Funnel
          </h1>

          <p className="lead text-muted mb-0">
            See how attendees move from opening Passport to completing stops, unlocking achievements, and finishing the experience.
          </p>
        </div>

        <div className="card shadow-sm">
          <div className="card-header bg-white">
            <strong>Passport Funnel</strong>
          </div>

          <div className="card-body">
            {events.length === 0 ? (
              <div className="alert alert-info mb-0">
                No engagement events have been recorded yet.
              </div>
            ) : (
              <div className="d-flex flex-column gap-3">
                {funnel.map((step, index) => (
                  <div
                    key={step.key}
                    className="border rounded p-3 bg-white"
                  >
                    <div className="d-flex justify-content-between align-items-start gap-3 mb-2">
                      <div>
                        <div className="fw-bold">
                          {index + 1}. {step.label}
                        </div>

                        <div className="small text-muted">
                          {step.description}
                        </div>
                      </div>

                      <div className="text-end">
                        <div className="h4 fw-bold mb-0">
                          {step.count}
                        </div>

                        <div className="small text-muted">
                          {step.percent}%
                        </div>
                      </div>
                    </div>

                    <div className="progress" style={{ height: "12px" }}>
                      <div
                        className="progress-bar bg-success"
                        role="progressbar"
                        style={{ width: `${step.percent}%` }}
                        aria-valuenow={step.percent}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="alert alert-light border mt-4 mb-0">
          <strong>BI Note:</strong> This funnel uses unique attendees per stage,
          so it measures progression rather than raw event volume.
        </div>
      </div>
    </section>
  );
};

export default EngagementFunnel;
